import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpInterceptor } from '@angular/common/http';
import { catchError, filter, finalize, from, map, mergeMap, Observable, shareReplay, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { MotifModal } from '@ey-xd/ng-motif';
import { environment } from 'src/environments/environment';
import { TokenModalComponent } from 'src/app/shared/tax-suite/components/token-modal/token-modal.component';
import { AuthService } from '../auth/auth.service';
import { SharepointAuthService } from '@src/app/shared/services/sharepoint/sharepoint-auth.service';

@Injectable({
  providedIn: 'root'
})
export class MsalIdTokenInterceptor implements HttpInterceptor {

  private __timer = null;
  private refreshingToken = false;
  private tokenSubject = new Subject<{ idToken: string, accessToken: string; }>();
  private unauthorizedRefresh$: Observable<{ idToken: string, accessToken: string; }> | null = null;

  constructor(
    private router: Router,
    private authMsalService: MsalService,
    private authService: AuthService,
    private motifModal: MotifModal,
    private sharepointAuthService: SharepointAuthService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.sharepointAuthService.getAccessToken()).pipe(
      switchMap((spToken) => this.handleRequest(request, next, spToken))
    );
  }

  private handleRequest(request: HttpRequest<any>, next: HttpHandler, spToken: string | null): Observable<HttpEvent<any>> {
    if (spToken) {
      request = request.clone({
        setHeaders: {
          'sp-token': spToken
        }
      });
    }

    const isBackendLogin = request.url.includes('/auth/login');
    const hasRetriedAfterUnauthorized = request.headers.has('x-auth-retry');

    return this.interceptRequest(request, next).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status !== 401 ||
          error.headers?.get('x-error-source') === 'sharepoint' ||
          isBackendLogin ||
          hasRetriedAfterUnauthorized ||
          !request.url.includes('ey.com')
        ) {
          return throwError(() => error);
        }

        const retryRequest = request.clone({
          setHeaders: { 'x-auth-retry': 'true' },
        });

        return this.refreshAfterUnauthorized().pipe(
          switchMap(({ idToken }) =>
            next.handle(
              retryRequest.clone({
                headers: retryRequest.headers.set(
                  'Authorization',
                  `Bearer ${idToken}`
                ),
              })
            )
          )
        );
      })
    );
  }

  private interceptRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (environment.envType != 'local') {

      if (request.url.includes('/auth/login')) {
        const token = localStorage.getItem('idToken');
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });

        return next.handle(request);

      } else if (request.url.includes('ey.com')) {

        // Si la url incluye estos dos textos, entonces es una llamada a un servicio de EY, por lo cual se gestiona el token
        const account =
          this.authMsalService.instance.getActiveAccount() ??
          this.authMsalService.instance.getAllAccounts()[0];
        const storageIdToken = localStorage.getItem('idToken');
        const tokenPayload = this.getTokenPayload(storageIdToken);

        // Internal routes can start their first request immediately after a
        // redirect. Wait for the shared startup promise instead of decoding a
        // token that has not been persisted yet.
        if (!account || !tokenPayload?.exp) {
          return from(this.authService.initializeAuthentication()).pipe(
            switchMap(() => this.acquireTokenAndRetry(request, next))
          );
        }

        const rq = {
          // scopes: ['https://graph.microsoft.com/.default'],
          scopes: ['user.read', 'openid', 'profile', 'email'],
          account
        };

        const accessToken = localStorage.getItem('accessToken');

        const accessTokenObject = this.getTokenPayload(accessToken);
        const timeInMinutesUntilExpireAccessToken = accessTokenObject?.exp
          ? ((accessTokenObject.exp * 1000 - new Date().getTime()) / 60000)
          : false;

        const timeInMinutesUntilExpire =
          (tokenPayload.exp * 1000 - new Date().getTime()) / 60000;

        console.log(`%c Time to expire idToken --> ${timeInMinutesUntilExpire} minutes`, 'background: #222; color: #bada55; font-size: 20px');
        console.log(`%c Time to expire accessToken --> ${timeInMinutesUntilExpireAccessToken} minutes`, 'background: #222; color: #bada55; font-size: 20px');

        // si faltan menos de 10 minutos para que el token expire, llamo a ssosilent
        if (timeInMinutesUntilExpire <= 15 || (timeInMinutesUntilExpireAccessToken && timeInMinutesUntilExpireAccessToken <= 15)) {

          if (this.refreshingToken) {
            return this.tokenSubject.pipe(
              filter(token => token != null),
              take(1),
              switchMap((data) => {
                console.log(`%c Reading token from subject`, 'background: #222; color: #bada55; font-size: 20px');
                const modifiedReq = request.clone({
                  headers: request.headers.set('Authorization', `Bearer ${data.idToken}`),
                });

                return next.handle(modifiedReq);
              })
            );
          } else {

            this.refreshingToken = true;
            this.tokenSubject.next(null);

            return from(
              this.authMsalService.instance.acquireTokenSilent({
                ...rq,
                forceRefresh: true
              })
            ).pipe(
              mergeMap((newToken) => {

                console.log('%c Reading token from [ssoSilent] (emitting to subject...)', 'background: #222; color: #bada55; font-size: 20px');

                const idToken = newToken.idToken;
                const accessToken = newToken.accessToken;

                // show a console log with bg red saying if the current idToken is the same as the one in the local storage
                if (idToken == localStorage.getItem('idToken')) {
                  console.log('%c Same token', 'background: red; color: white');
                } else {
                  console.log('%c Different token', 'background: green; color: white');
                }

                localStorage.setItem('idToken', idToken);
                localStorage.setItem('accessToken', accessToken);

                const modifiedReq = request.clone({
                  headers: request.headers.set('Authorization', `Bearer ${idToken}`),
                });

                return from(this.loginTaxSuiteBackend()).pipe(
                  mergeMap(() => {
                    this.resetTimer();
                    this.refreshingToken = false;
                    // Release queued API calls only after the backend has
                    // established its session for the renewed token.
                    this.tokenSubject.next({ idToken, accessToken });
                    return next.handle(modifiedReq);
                  })
                );
              }),
              catchError((error) => {
                console.log('%c Error in ssoSilent', 'background: red; color: white; font-size: 20px');
                console.error(error);
                localStorage.setItem('loggedIn', 'false');
                localStorage.setItem('refreshToken', 'false');
                this.refreshingToken = false;
                // Do not leave concurrent requests waiting forever when either
                // the MSAL refresh or the backend login fails.
                this.tokenSubject.error(error);
                this.tokenSubject = new Subject<{ idToken: string, accessToken: string; }>();

                // Si obtenemos un error en el ssoSilent, significa que el token ha expirado y no se puede refrescar, por lo que reautenticamos al usuario.
                if (timeInMinutesUntilExpire <= 0 || (timeInMinutesUntilExpireAccessToken && timeInMinutesUntilExpireAccessToken <= 0)) {
                  this.loginRedirect();
                }

                throw error;
              })
            );

          }


        } else {

          return from(this.authMsalService.instance.acquireTokenSilent(rq))
            .pipe(
              catchError((error) => {
                console.log('%c Error in acquireTokenSilent', 'background: red; color: white; font-size: 20px');
                console.error(error);
                localStorage.setItem('loggedIn', 'false');
                localStorage.setItem('refreshToken', 'false');
                //this.loginRedirect();
                throw error;
              }),
              mergeMap((result) => {
                if (result) {

                  localStorage.setItem('accessToken', result.accessToken);

                  const modifiedReq = request.clone({
                    headers: request.headers.set('Authorization', `Bearer ${result.idToken}`),
                  });

                  if (result.idToken !== localStorage.getItem('idToken')) {

                    console.log('%c Different token 2', 'background: green; color: white');

                    localStorage.setItem('idToken', result.idToken);
                    localStorage.setItem('accessToken', result.accessToken);

                    return from(this.loginTaxSuiteBackend()).pipe(
                      mergeMap(() => {
                        this.resetTimer();
                        return next.handle(modifiedReq);
                      })
                    );
                  } else {

                    console.log('%c Same token 2', 'background: red; color: white');
                    localStorage.setItem('idToken', result.idToken);
                    this.resetTimer();
                    return next.handle(modifiedReq);
                  }
                }

              }),
              catchError((error) => {
                console.log('%c Error calling to TAX SUITE BACKEND', 'background: red; color: white; font-size: 20px');
                throw error;
              })

            );

        }
      } else {
        return next.handle(request);
      }

    }

    // for localhost
    return next.handle(request);

  }

  private resetTimer() {

    console.log('%c Resetting timer', 'background: #222; color: #bada55; font-size: 20px');

    if (this.__timer) {
      clearInterval(this.__timer);
    }

    const twoMinutes = 2 * 60 * 1000;

    let modal = null;
    // Si el usuario no hace nada en 2 minutos, se revisa si el token está a punto de expirar y se muestra el modal para extender la sesión
    this.__timer = setInterval(() => {

      console.log('%c Executing interval', 'background: #222; color: #bada55; font-size: 20px');

      const token = localStorage.getItem('idToken');
      const tokenObject = this.getTokenPayload(token);

      if (!tokenObject?.exp) {
        return;
      }

      const timeInSeconds = (tokenObject.exp * 1000 - new Date().getTime()) / 1000;
      const timeInMinutes = timeInSeconds / 60;

      console.log(`IdToken will expire in ${timeInMinutes} minutes`);

      const fiveMinutesInSeconds = 5 * 60;

      // Si el token expira en menos de 7 minutos y no hay un modal abierto, se muestra el modal
      if (timeInMinutes > 0 && timeInMinutes < 7 && modal === null) {

        console.log('%c Showing modal', 'background: #222; color: #bada55; font-size: 20px');

        modal = this.motifModal.open(TokenModalComponent, { closeOnNavigation: false, disableClose: true });
        modal.componentInstance.timeToExpire = fiveMinutesInSeconds;
        modal.componentInstance.token = tokenObject;
        modal.componentInstance.startTimer();

        const rq = {
          // scopes: ['https://graph.microsoft.com/.default'],
          scopes: ['user.read', 'openid', 'profile', 'email'],
          account: this.authMsalService.instance.getAllAccounts()[0]
        };

        modal.componentInstance.extendSessionEvent.subscribe(async () => {

          modal.close();

          const isTokenExpired = tokenObject.exp * 1000 - new Date().getTime() <= 0;

          if (isTokenExpired) {
            localStorage.setItem('refreshToken', 'false');
            this.refreshToken();
          } else {

            const newToken = await this.authMsalService.instance.ssoSilent(rq);
            const idToken = newToken.idToken;
            localStorage.setItem('idToken', idToken);
            localStorage.setItem('accessToken', newToken.accessToken);
            await this.loginTaxSuiteBackend();


            const diffInMinutesIdToken = (JSON.parse(atob(idToken.split('.')[1])).exp * 1000 - new Date().getTime()) / 60000;
            console.log(`%c Time to expire NEW idToken --> ${diffInMinutesIdToken} minutes`, 'background: #222; color: #bada55; font-size: 20px');

            this.resetTimer();
          }

        });

        modal.componentInstance.refreshPageEvent.subscribe(() => {
          modal.close();
          localStorage.setItem('refreshToken', 'false');
          this.refreshToken();
        });

      }



    }, twoMinutes);


  }

  private acquireTokenAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const account =
      this.authMsalService.instance.getActiveAccount() ??
      this.authMsalService.instance.getAllAccounts()[0];

    if (!account) {
      return next.handle(request);
    }

    return from(
      this.authMsalService.instance.acquireTokenSilent({
        scopes: ['user.read', 'openid', 'profile', 'email'],
        account,
      })
    ).pipe(
      switchMap((result) => {
        this.authMsalService.instance.setActiveAccount(result.account);
        localStorage.setItem('idToken', result.idToken);
        localStorage.setItem('accessToken', result.accessToken);
        // Reentra directamente en interceptRequest: el sp-token y el manejo de
        // reintento en 401 ya se aplicaron en la llamada original a handleRequest.
        return this.interceptRequest(request, next);
      })
    );
  }

  private refreshAfterUnauthorized(): Observable<{ idToken: string, accessToken: string; }> {
    if (this.unauthorizedRefresh$) {
      return this.unauthorizedRefresh$;
    }

    const account =
      this.authMsalService.instance.getActiveAccount() ??
      this.authMsalService.instance.getAllAccounts()[0];

    if (!account) {
      this.loginRedirect();
      return throwError(() => new Error('No active MSAL account available to refresh the session.'));
    }

    this.unauthorizedRefresh$ = from(
      this.authMsalService.instance.acquireTokenSilent({
        scopes: ['user.read', 'openid', 'profile', 'email'],
        account,
        forceRefresh: true,
      })
    ).pipe(
      switchMap((result) => {
        const token = {
          idToken: result.idToken,
          accessToken: result.accessToken,
        };

        this.authMsalService.instance.setActiveAccount(result.account);
        localStorage.setItem('idToken', token.idToken);
        localStorage.setItem('accessToken', token.accessToken);

        return from(this.loginTaxSuiteBackend()).pipe(
          map(() => token)
        );
      }),
      tap(() => {
        this.resetTimer();
        localStorage.setItem('loggedIn', 'true');
        localStorage.setItem('refreshToken', 'false');
      }),
      catchError((error) => {
        localStorage.setItem('loggedIn', 'false');
        localStorage.setItem('refreshToken', 'false');
        this.loginRedirect();
        return throwError(() => error);
      }),
      finalize(() => {
        this.unauthorizedRefresh$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.unauthorizedRefresh$;
  }

  private getTokenPayload(
    token: string | null
  ): { exp?: number; } | null {
    if (!token) {
      return null;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length < 2) {
      return null;
    }

    try {
      const payload = tokenParts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  async loginTaxSuiteBackend() {
    return await this.authService.apiLogin({ lastLoginCountry: '' }).toPromise();
  }

  completeTokenRefresh(res) {
    if (res && res.active) {
      localStorage.setItem('userLogged', JSON.stringify(res));
      localStorage.setItem('refreshToken', 'false');
    } else {
      this.router.navigate(['/tax-suite'], {
        queryParams: {
          errorType: 'Unauthorized'
        }
      });
    }
  }

  refreshToken() {
    let refreshToken = JSON.parse(localStorage.getItem('refreshToken'));
    if (!refreshToken) {
      console.log("Retrieving token silent");
      localStorage.setItem('refreshToken', 'true');

      const request = {
        // scopes: ['https://graph.microsoft.com/.default'],
        scopes: ['user.read', 'openid', 'profile', 'email'],
        account: this.authMsalService.instance.getAllAccounts()[0]
      };
      this.authMsalService.instance.ssoSilent(request).then(async result => {
        if (result) {
          if (result.idToken == localStorage.getItem('idToken') || !result.idToken || !localStorage.getItem('idToken')) {
            console.log("Equal tokens -> Redirect");
            this.loginRedirect();
          } else {
            console.log("Different tokens -> Token refreshed");
            this.authMsalService.instance.setActiveAccount(result.account);
            localStorage.setItem('idToken', result.idToken);
            localStorage.setItem('accessToken', result.accessToken);
            let res = await this.loginTaxSuiteBackend();
            this.completeTokenRefresh(res);
          }
        }
      }).catch(error => {
        console.log("SSO Error: "); console.log(error);
        localStorage.setItem('refreshToken', 'false');
        localStorage.setItem('loggedIn', 'false');
        this.loginRedirect();
      });
    } else {
      console.log("El ssoSilent ya se está ejecutando");
    }
  }

  loginRedirect() {
    void this.authService.loginRedirect().catch((error) => {
      console.error('Login redirect error:', error);
    });
  }

}
